<script setup lang="ts">
import { computed, toRefs, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { Address } from '@/types/address'
import { Phone } from '@/types/phone'
import { Email } from '@/types/email'
import { CorporateWithContactData } from '@/types/corporative'

interface IDetailDataSection {
  title: string
  icon: string
  field: 'addresses' | 'emails' | 'phones' | 'about'
  emptyState: string
}
const { t } = useI18n()
const props = defineProps<{
  data: IDetailDataSection
  corporativeAccount: Partial<CorporateWithContactData>
}>()

const { data, corporativeAccount } = toRefs(props)
const { icon, title, field, emptyState } = data.value

const computedTitle = computed(() => t(title))
const isStringOnlyField = computed(() => field === 'about')

// --- "Ver mais" ABOUT ---
const showFullAbout = ref(false)
const maxAboutChars = 260

const content = computed(() => {
  const getLabel = (
    field: 'phones' | 'emails' | 'addresses',
    item: Address | Phone | Email
  ) => {
    const defaultLabelForMainField = {
      phones: 'corporate.detail.defaultMainPhoneLabel',
      emails: 'corporate.detail.defaultMainEmailLabel',
      addresses: 'general.defaultMainAdressLabel',
    }
    const defaultLabelForSecondaryField = {
      phones: 'corporate.detail.defaultSecondaryPhoneLabel',
      emails: 'corporate.detail.defaultSecondaryEmailLabel',
      addresses: 'corporate.detail.defaultSecondaryAdressLabel',
    }

    const hasLabel = (obj: unknown): obj is { label?: string | null } =>
      typeof obj === 'object' && obj !== null && 'label' in obj
    const hasMain = (obj: unknown): obj is { main?: boolean | null } =>
      typeof obj === 'object' && obj !== null && 'main' in obj

    if (!hasLabel(item) || !item.label || item.label.includes('label')) {
      if (hasMain(item) && item.main) {
        return t(defaultLabelForMainField[field])
      } else {
        return t(defaultLabelForSecondaryField[field])
      }
    }

    return item.label
  }

  if (!corporativeAccount.value) return ''

  const content = (
    corporativeAccount.value as Record<
      'addresses' | 'phones' | 'emails',
      (Address | Phone | Email)[]
    > &
      Record<'about', string>
  )[field]

  if (typeof content === 'string') return content

  if (Array.isArray(content) && content.length === 0) {
    return t(emptyState)
  }

  return (
    (content as (Address | Phone | Email)[])?.map((item) => ({
      label: getLabel(field as 'addresses' | 'emails' | 'phones', item),
      main: 'main' in item ? item.main : false,
      value: getItemValueByField(
        item,
        field as 'addresses' | 'emails' | 'phones'
      ),
    })) || []
  )
})

const isAboutTruncated = computed(
  () =>
    isStringOnlyField.value &&
    typeof content.value === 'string' &&
    content.value.length > maxAboutChars
)

const aboutTextToShow = computed(() => {
  if (!isStringOnlyField.value || typeof content.value !== 'string') {
    return content.value
  }

  if (!isAboutTruncated.value || showFullAbout.value) {
    return content.value
  }

  return content.value.slice(0, maxAboutChars) + '...'
})

function getItemValueByField(
  item: Address | Phone | Email,
  field: 'addresses' | 'emails' | 'phones'
) {
  if (field === 'addresses') {
    const addressItem = item as Address
    const getLineOrEmptyQuotes = (line: string | null) =>
      line ? line + '\n' : ''

    const addressLines = [
      addressItem.line1,
      addressItem.line2,
      addressItem.line3,
    ].reduce((acc: string, el?: string | null) => {
      if (el && el.trim() !== '') {
        return acc + getLineOrEmptyQuotes(String(el))
      }
      return acc
    }, '')

    const finalLine = [
      addressItem.postalCode,
      addressItem.cityName && addressItem.stateCode
        ? `${addressItem.cityName} - ${addressItem.stateCode}`
        : '',
      addressItem.countryName,
    ]
      .filter((e) => !!e)
      .reduce((acc, el, index, array) => {
        if (index === array.length - 1) return acc + String(el)
        return acc + String(el) + `<span class="text-green"> | </span>`
      }, '')

    return `${addressLines}${finalLine}`
  } else if (field === 'phones') return (item as Phone).number
  else return (item as Email).email
}
</script>

<template>
  <v-container class="py-2">
    <!-- Header: ícone + título -->
    <div class="dds-header">
      <div class="dds-header__icon">
        <v-img :src="icon" alt="" max-height="20" max-width="24" contain />
      </div>

      <div class="dds-header__title">
        <h6 class="text-subtitle-1 font-weight-bold mb-0">
          {{ computedTitle }}
        </h6>
      </div>
    </div>

    <!-- Body: coluna "main/favorite" + conteúdo -->
    <div class="dds-body mt-2">
      <!-- Coluna fixa (favorite) para listas -->
      <div class="dds-body__maincol"></div>

      <!-- Conteúdo -->
      <div
        class="dds-body__content"
        :class="{
          'border-grey-lighten-2 border-b border-opacity-100 pb-2':
            !isStringOnlyField,
          showFullAbout,
        }"
        style="border-style: solid"
      >
        <!-- vazio -->
        <div v-if="content === ''" class="py-2">
          {{ $t(emptyState) }}
        </div>

        <!-- ABOUT -->
        <div v-else-if="typeof content === 'string'" class="text-body-1">
          {{ isStringOnlyField ? aboutTextToShow : content }}

          <button
            v-if="isStringOnlyField && isAboutTruncated"
            type="button"
            class="text-button ms-2 text-green"
            style="border: none; background: transparent; cursor: pointer"
            @click="showFullAbout = !showFullAbout"
          >
            {{ showFullAbout ? t('events.seeLess') : t('events.seeMore') }}
          </button>
        </div>

        <!-- LISTAS -->
        <div v-else>
          <div
            v-for="item in content"
            :key="String(item.value) + String(item.label)"
            class="dds-item"
          >
            <!-- coluna fixa p/ ★ -->
            <div class="dds-item__star">
              <v-icon v-if="item.main" size="18">mdi-star</v-icon>
            </div>

            <!-- label + value -->
            <div class="dds-item__body">
              <h6
                class="text-caption font-italic mb-1"
                style="
                  white-space: nowrap;
                  overflow: hidden;
                  text-overflow: ellipsis;
                "
                :title="item?.label"
              >
                {{ item?.label }}
              </h6>

              <p
                class="text-body-2 font-weight-bold mb-0"
                style="white-space: pre-line"
                v-html="item.value"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </v-container>
</template>

<style scoped>
.dds-header {
  display: flex;
  gap: 12px;
  align-items: center;
}

.dds-header__icon {
  width: 40px;
  min-width: 40px;
  display: flex;
  justify-content: center;
}

.dds-header__title {
  flex: 1;
  min-width: 0;
}

.dds-body {
  display: flex;
  gap: 12px;
  align-items: flex-start;
}

.dds-body__maincol {
  width: 40px;
  min-width: 40px;
  display: flex;
  justify-content: center;
}

.dds-body__content {
  flex: 1;
  min-width: 0;
}

.dds-item {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  margin-bottom: 16px;
}

.dds-item__star {
  width: 24px;
  min-width: 24px;
  display: flex;
  justify-content: center;
  padding-top: 2px;
}

.dds-item__body {
  flex: 1;
  min-width: 0;
}
</style>
